import { memo, useEffect, useRef, useState } from "react";
import { getData } from "../../../API/GlobalApi";
import SpinnerLoader from "../../../Utils/Spinner";
import "../../../assets/css/Modal.css";
import exporticon from '../../../assets/icons/export-data.svg';
import jsPDF from "jspdf";
import "jspdf-autotable";

// Chevron icons
const ChevronRightIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none"
    xmlns="http://www.w3.org/2000/svg" style={{ marginRight: "6px" }}>
    <path d="M7 5l5 5-5 5"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDownIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none"
    xmlns="http://www.w3.org/2000/svg" style={{ marginRight: "6px" }}>
    <path d="M5 7l5 5 5-5"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ExpandedContent = memo(({ specialization }) => {
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSemester, setExpandedSemester] = useState(null);
  const [sections, setSections] = useState({});
  const [expandedSection, setExpandedSection] = useState(null);
  const [students, setStudents] = useState([]);
  const [expandedStudents, setExpandedStudents] = useState({});
  const [semesterStudentCount, setSemesterStudentCount] = useState({});
  const [searchText, setSearchText] = useState({});
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);



  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!specialization?.id || fetchedRef.current) return;

    const fetchSemesters = async () => {
      setLoading(true);
      try {
        const data = await getData(
          `finance/specializations/${specialization.id}/academic-groups/`
        );


        setSemesters(data || []);
        const studentCounts = {};
        (data || []).forEach((semester) => {
          const count = (semester.lags || []).reduce(
            (sum, section) => sum + (section.students?.length || 0),
            0
          );
          studentCounts[semester.id] = count;
        });
        setSemesterStudentCount(studentCounts);
      } catch (err) {
        console.error("Error fetching semesters", err);
      } finally {
        setLoading(false);
      }
      // ✅ Mark this specialization as fetched
      fetchedRef.current = true;
    };

    fetchSemesters();
  }, [specialization?.id,]);




  // Fetch sections for a semester when expanded
  const toggleSemester = async (sem) => {
    if (expandedSemester === sem.id) {
      setExpandedSemester(null);
      return;
    }

    setExpandedSemester(sem.id);
    if (!sections[sem.id]) {
      setLoadingSections(true);
      try {
        const res = await getData(
          `finance/specializations/${specialization.id}/academic-groups/${sem.id}/least-academic-groups/`
        );

        setSections((prev) => ({ ...prev, [sem.id]: res || [] }));

        setSemesterStudentCount((prev) => ({
          ...prev,
          [sem.id]: (res || []).reduce(
            (sum, sec) => sum + (sec.students?.length || 0),
            0
          ),
        }));
      } catch (err) {
        console.error(`Error loading sections for semester ${sem.id}`, err);
      } finally {
        setLoadingSections(false);
      }
    }
  };
  // Toggle Section expand
  const toggleSection = async (sec, semId) => {
    if (expandedSection === sec.id) {
      setExpandedSection(null);
      setStudents([]);
      return;
    }
    setExpandedSection(sec.id);
    setLoadingStudents(true);
    const res = await getData(
      `finance/specializations/${specialization.id}/academic-groups/${semId}/least-academic-groups/${sec.id}/`
    );
    const studentsData = Array.isArray(res) ? res[0]?.students || [] : res?.students || [];
    setStudents(studentsData);
    setExpandedStudents({});
    setLoadingStudents(false);
  };

  const toggleStudent = (id) => {
    setExpandedStudents((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getTotalAmount = (st) => {
    if (!st.fee_schedules?.length) return 0;
    return st.fee_schedules.reduce(
      (sum, fee) => sum + parseFloat(fee.amount || 0), 0
    );
  };

  /** ---------- PDF EXPORT FUNCTIONS ---------- **/
  // Common formatter for amount
  const formatINR = (val) => {
    const num = cleanNumber(val);
    return `INR ${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Common helper
  const cleanNumber = (val) => {
    if (!val) return 0;
    return parseFloat(String(val).replace(/[^\d.-]/g, "")) || 0;
  };

  // 1. Semester-wise export

  const exportSemesterPDF = (sem) => {

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Student Fee Details - ${specialization?.specialization || ""}`, 14, 16);
    doc.text(`Semester: ${sem.name}`, 14, 24);

    const tableData = [];

    (sem.lags || []).forEach((sec) => {
      (sec.students || []).forEach((st) => {
        if (st.fee_schedules?.length) {
          st.fee_schedules.forEach((fee, idx) => {
            tableData.push([
              idx === 0 ? sec.name : "", // only first row shows Section
              idx === 0 ? `${st.full_name} (${st.permanent_registration_number})` : "",
              fee.fee_type_head,
              formatINR(fee.amount),
            ]);
          });
        } else {
          tableData.push([
            sec.name,
            `${st.full_name} (${st.permanent_registration_number})`,
            "No Fee Head",
            "INR 0.00",
          ]);
        }
      });
    });


    doc.autoTable({
      head: [["Section", "Student (Reg. No)", "Fee Head", "Total Impose Amount"]],
      body: tableData,
      startY: 32,
      styles: { fontSize: 10, cellWidth: "wrap" },
      headStyles: { fillColor: [127, 86, 218] },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 55 },
        2: { cellWidth: 60 },
        3: { cellWidth: 40, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "head" && data.column.index === 3) {
          data.cell.styles.halign = "right";
        }
      },
    });

    doc.save(`student_fee_details_semester_${sem.name}.pdf`);
  };

  // 2. Section-wise export
  const exportSectionPDF = (sem, sec) => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Student Fee Details - ${specialization?.specialization || ""}`, 14, 16);
    doc.text(`Semester: ${sem.name} | Section: ${sec.name}`, 14, 24);

    const tableData = [];
    (sec.students || []).forEach((st) => {
      if (st.fee_schedules?.length) {
        st.fee_schedules.forEach((fee, idx) => {
          tableData.push([
            idx === 0 ? `${st.full_name} (${st.permanent_registration_number})` : "",
            fee.fee_type_head,
            formatINR(fee.amount),
          ]);
        });
      } else {
        tableData.push([
          `${st.full_name} (${st.permanent_registration_number})`,
          "No Fee Head",
          "INR 0.00",
        ]);
      }
    });

    doc.autoTable({
      head: [["Student (Reg. No)", "Fee Head", "Total Impose Amount"]],
      body: tableData,
      startY: 32,
      styles: { fontSize: 10, cellWidth: "wrap" },
      headStyles: { fillColor: [127, 86, 218] },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 60 },
        2: { cellWidth: 40, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "head" && data.column.index === 2) {
          data.cell.styles.halign = "right";
        }
      },
    });

    doc.save(`student_fee_details_${sem.name}_${sec.name}.pdf`);
  };

  // 3. Student-wise export
  const exportStudentPDF = (sem, sec, st) => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Student Fee Details - ${specialization?.specialization || ""}`, 14, 16);
    doc.text(`Semester: ${sem.name} | Section: ${sec.name}`, 14, 24);
    doc.text(`Student: ${st.full_name} (${st.permanent_registration_number})`, 14, 32);

    const tableData = [];
    if (st.fee_schedules?.length) {
      st.fee_schedules.forEach((fee) => {
        tableData.push([fee.fee_type_head, formatINR(fee.amount)]);
      });
    } else {
      tableData.push(["No Fee Head", "INR 0.00"]);
    }

    doc.autoTable({
      head: [["Fee Head", "Total Impose Amount"]],
      body: tableData,
      startY: 40,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [127, 86, 218] },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 40, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "head" && data.column.index === 1) {
          data.cell.styles.halign = "right";
        }
      },
    });

    doc.save(`student_fee_details_${st.permanent_registration_number}.pdf`);
  };

  /** ---------- RENDER ---------- **/
  if (loading) return <SpinnerLoader />;

  return (
    <div style={{ padding: "12px", background: "#fafafa" }}>
      {!loading && semesters.map((sem) => (
        <div key={sem.id} style={{ marginBottom: "10px" }}>
          {/* Semester Header */}
          <div
            style={{
              padding: "8px 12px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              backgroundColor: expandedSemester === sem.id ? "#e6f0ff" : "#f9f9f9",
              fontWeight: 600,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              onClick={() => toggleSemester(sem)}
              style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            >
              {expandedSemester === sem.id ? <ChevronDownIcon /> : <ChevronRightIcon />}
              {sem.name}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontWeight: 500, color: "#555" }}>
                Total Students: {semesterStudentCount[sem.id] || 0}
              </span>

              <button
                className="filter-btn"
                style={{ background: "white", border: "1px solid black", height: "33px" }}
                onClick={() => exportSemesterPDF(sem)}
                title="Export Semester wise students"
              >
                <img src={exporticon} alt="Export icon" />
              </button>
            </div>
          </div>

          {/* Sections */}
          {expandedSemester === sem.id && (
            <div style={{ marginTop: "8px", marginLeft: "20px" }}>
              {loadingSections ? (
                <SpinnerLoader />
              ) : (sections[sem.id] || []).map((sec) => (
                <div key={sec.id} style={{ marginBottom: "6px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 8px",
                      borderLeft: "2px solid #7F56DA",
                      backgroundColor: expandedSection === sec.id ? "#f0eaff" : "transparent",
                    }}
                  >
                    <div
                      onClick={() => toggleSection(sec, sem.id)}
                      style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                    >
                      {expandedSection === sec.id ? <ChevronDownIcon size={12} /> : <ChevronRightIcon size={12} />}
                      {sec.name} (Students: {sec.students?.length || 0})
                    </div>

                    <button
                      className="filter-btn"
                      style={{ background: "white", border: "1px solid black", height: "26px" }}
                      onClick={() => exportSectionPDF(sem, sec)}
                      title="Export sections wise students"
                    >
                      <img src={exporticon} alt="Export section" style={{ height: "16px" }} />
                    </button>
                  </div>

                  {/* Students */}
                  {expandedSection === sec.id && (
                    <div style={{ marginTop: "6px", marginLeft: "16px" }}>
                      {loadingStudents ? (
                        <SpinnerLoader />
                      ) : students.length > 0 ? (
                        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                          <table className="table table-bordered imposeFeeTables">
                            <thead>
                              <tr>
                                <th>Student Name (Reg. No)</th>
                                <th className="text-end">Total Imposed Amount</th>
                                <th className="text-end">Export</th>
                              </tr>
                            </thead>
                            <tbody>
                              {students
                                .filter((st) => {
                                  const search = (searchText[sec.id] || "").toLowerCase();
                                  return (
                                    st.full_name.toLowerCase().includes(search) ||
                                    st.permanent_registration_number.toLowerCase().includes(search)
                                  );
                                })
                                .map((st) => {
                                  const total = getTotalAmount(st);
                                  const isExpanded = expandedStudents[st.user_id];
                                  return (
                                    <>
                                      <tr key={st.user_id}>
                                        <td
                                          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                                          onClick={() => toggleStudent(st.user_id)}
                                        >
                                          {isExpanded ? <ChevronDownIcon size={12} /> : <ChevronRightIcon size={12} />}
                                          {st.full_name} ({st.permanent_registration_number})
                                        </td>
                                        <td className="text-end">₹ {total.toFixed(2)}</td>
                                        <td className="text-end d-flex justify-content-end">
                                          <button
                                            className="filter-btn"
                                            style={{ background: "white", border: "1px solid black", height: "26px" }}
                                            onClick={() => exportStudentPDF(sem, sec, st)}
                                            title="Export student Fee heads"
                                          >
                                            <img src={exporticon} alt="Export student" style={{ height: "16px" }} />
                                          </button>
                                        </td>
                                      </tr>

                                      {isExpanded &&
                                        (st.fee_schedules?.length > 0 ? (
                                          st.fee_schedules.map((fee) => (
                                            <tr key={`${st.user_id}-${fee.fee_type_head_pk}`}>
                                              <td style={{ paddingLeft: "32px" }}>{fee.fee_type_head}</td>
                                              <td className="text-end">₹ {parseFloat(fee.amount).toFixed(2)}</td>
                                              <td></td>
                                            </tr>
                                          ))
                                        ) : (
                                          <tr>
                                            <td style={{ paddingLeft: "32px" }}>No Fee Head</td>
                                            <td className="text-end">INR 0.00</td>
                                            <td></td>
                                          </tr>
                                        ))}
                                    </>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div>No students found</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
});

export default ExpandedContent;